import { IMAGE_LIBRARY_TYPE } from "@/shared/imageLibrary/types";
import { useModalMeta } from "@/shared/atoms/useModal";
import useBranchId from "@/shared/hooks/useBranchId";
import useCategoryHook from "@/shared/hooks/useCategoryHook";
import { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  cleanCharFromObject,
  getMenuImageUrl,
} from "../../shared/utils/helpers";
import Alert from "../Alert/Alert";
import Button from "../Button/Button";
import Modal, { ModalProps } from "../Modal/Modal";
import ModalTitle from "../Modal/ModalTitle";
import CategoryModalStandard from "../Menu/CategoryModalStandard";
import IconToolTip from "../Icon/IconToolTip";
import CategoryModalAdvance from "../Menu/CategoryModalAdvance";
import { CategoryType } from "@/shared/utils/enum";

const MenuCategoryModal = ({ onExit, ...props }: ModalProps) => {
  const { t } = useTranslation();
  const [{ defaultValue = {}, isEdit }] = useModalMeta();
  const branch = useBranchId();
  const {
    categoryCreate,
    categoryUpdate,
    uploadImage,
    uploadImageToCloudinary,
    getCloudinaryInfo,
    updateCloudinaryInfo,
  } = useCategoryHook();
  const [preview, setPreview] = useState(
    getMenuImageUrl(
      defaultValue?.image_cloudinary_id,
      branch,
      IMAGE_LIBRARY_TYPE.category
    )
  );
  const [selectedFile, setSelectedFile] = useState(null);
  const methods = useForm();
  const { handleSubmit } = methods;
  const [isStandardSettings, setIsStandardSettings] = useState(true);

  const onFormSubmit = async (data, event) => {
    const submitData = {
      ...defaultValue,
      ...data,
      back_ground_color_per_target: cleanCharFromObject(
        data.back_ground_color_per_target,
        "%"
      ),
      text_color_per_target: cleanCharFromObject(
        data.text_color_per_target,
        "%"
      ),
    };

    if (isEdit) {
      let image_cloudinary_id: any = preview
        ? defaultValue?.image_cloudinary_id
        : undefined;
      if (selectedFile) {
        const fileId = defaultValue?.image_cloudinary_id
          ?.split("/")?.[1]
          ?.split(".")?.[0];
        image_cloudinary_id = await uploadImage(
          "item-image",
          selectedFile,
          fileId
        );
      }
      categoryUpdate.mutateAsync(
        { ...submitData, image_cloudinary_id },
        {
          onSuccess: () => onExit(),
        }
      );
    } else {
      let image_cloudinary_id: any;
      if (selectedFile) {
        image_cloudinary_id = await uploadImage("item-image", selectedFile);
      }
      categoryCreate.mutateAsync(
        {
          ...submitData,
          virtual_item_type: Number(
            data.virtual_item_type ?? CategoryType.NORMAL
          ),
          position: 0,
          parenItemId: 0,
          is_active: true,
          item_type: "1",
          image_cloudinary_id,
        },
        {
          onSuccess: () => {
            event.target.reset();
            onExit();
          },
        }
      );
    }
  };
  return (
    <Modal {...props} onExit={onExit}>
      <div className="relative">
        <ModalTitle>
          {isEdit ? t("menu.categoryUpdate") : t("menu.categoryCreate")}
        </ModalTitle>

        {(categoryCreate.isError || categoryUpdate.isError) && (
          <div className="mb-12">
            <Alert>{t("errorMessage")}</Alert>
          </div>
        )}
        <div
          onClick={() => setIsStandardSettings(!isStandardSettings)}
          className="absolute -right-3 top-12 flex justify-end"
        >
          <IconToolTip
            text={
              isStandardSettings
                ? t("menu.advanceSettings")
                : t("menu.standardSettings")
            }
            variants={isStandardSettings ? "gear" : "reply"}
            className="fill-primary"
          />
        </div>

        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onFormSubmit)} className="mt-[1rem]">
            {isStandardSettings ? (
              <CategoryModalStandard
                defaultValue={defaultValue}
                selectedFile={selectedFile}
                setSelectedFile={setSelectedFile}
                preview={preview}
                setPreview={setPreview}
              />
            ) : (
              <CategoryModalAdvance category={defaultValue} />
            )}
            <Button
              isPending={
                categoryCreate.isPending ||
                categoryUpdate.isPending ||
                uploadImageToCloudinary.isPending ||
                getCloudinaryInfo.isPending ||
                updateCloudinaryInfo.isPending
              }
            >
              {t("save")}
            </Button>
          </form>
        </FormProvider>
      </div>
    </Modal>
  );
};

export default MenuCategoryModal;
